import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { persistReducer } from 'redux-persist';
import storage from './storage';

export interface MediaAttachment {
  id: string;
  assessment_id?: string; // Optional - set after backend saves
  type: 'photo' | 'audio' | 'video';
  url?: string; // Optional - only set after backend saves
  fileName: string; // Camel case for frontend consistency
  fileSize: number; // Camel case
  mimeType: string; // Camel case
  base64Data?: string; // NEW: Store base64 encoded file data before submit
  touchpointId?: string; // Track which touchpoint this media belongs to
  duration_sec?: number; // For audio/video
  uploadedAt?: string; // Changed from created_at, camel case
}

export interface AssessmentItem {
  id: string;
  assessment_id: string; // Backend field
  question_id_snapshot: number; // Backend snapshot approach
  department_id_snapshot: number; // Backend snapshot
  touchpoint_id_snapshot: number; // Backend snapshot
  answer: 'yes' | 'no' | 'na'; // Backend enum
  comment?: string; // Backend field
  answered_at?: string; // Timestamp when answer was provided (mandatory per touchpoint) - ISO 8601 format
  weight: number; // Backend field (default 1.0)
  created_at: string; // Backend timestamps
  updated_at: string;

  // Frontend-specific fields (derived/display)
  standard: string;
  description: string;
  photos?: string[];
  audioRecording?: string;
  timestamp?: string;

  // Backend media will be separate table
  media?: Array<{
    id: string;
    type: 'photo' | 'audio';
    url: string;
    duration_sec?: number;
    size_bytes?: number;
  }>;
}

export interface Assessment {
  id: string;
  tenant_id: number; // Backend alignment
  property_id: number; // Backend field name
  type: 'internal' | 'external'; // Backend field
  status: 'draft' | 'submitted' | 'approved' | 'locked'; // Extended workflow states
  round_label?: string; // Backend field
  assessed_on: string; // Backend field for trend analysis (DATETIME)
  overall_score: number; // Backend field name
  dept_scores?: Record<string, number>; // Backend JSON field
  tp_scores?: Record<string, number>; // Backend JSON field
  created_at: string; // Backend timestamps
  updated_at: string;

  // Enhanced workflow fields
  submitted_at?: string;
  approved_at?: string;
  approved_by?: number;
  locked_at?: string;
  locked_by?: number;
  rejection_reason?: string;

  // Task and assignment fields
  task_id?: string;
  auditor_id: number;
  template_id: string;
  department_id: string;
  touchpoint_id: string;

  // Media and evidence
  media_attachments: MediaAttachment[];
  deletedMediaIds?: string[]; // Track media IDs to delete on submit

  // Frontend-specific fields (derived from relationships)
  propertyName?: string;
  auditorName?: string;
  department?: string;
  touchpoint?: string;
  items: AssessmentItem[];
  metadata?: {
    location?: string;
    weather?: string;
    timeOfDay?: string;
    customFields?: Record<string, unknown>;
  };
}

export interface AssessmentTemplate {
  id: string;
  name: string;
  description: string;
  department: string;
  touchpoint: string;
  items: Omit<
    AssessmentItem,
    'id' | 'result' | 'comment' | 'photos' | 'audioRecording' | 'timestamp'
  >[];
  isActive: boolean;
  createdBy: number;
  createdAt: string;
  updatedAt: string;
}

interface TouchpointMediaCounts {
  departmentId: number;
  touchpointId: number;
  currentImages: number;
  currentAudios: number;
  pendingImages: number; // Images uploaded but not yet submitted
  pendingAudios: number; // Audios uploaded but not yet submitted
}

interface AssessmentsState {
  assessments: Assessment[];
  templates: AssessmentTemplate[];
  currentAssessment: Assessment | null;
  selectedTemplate: AssessmentTemplate | null;
  // Media counts per touchpoint (loaded from backend + pending uploads)
  touchpointMediaCounts: Record<string, TouchpointMediaCounts>; // key: `${assessmentId}_${departmentId}_${touchpointId}`
  filters: {
    propertyId?: number;
    auditorId?: number;
    department: string[];
    status: Assessment['status'][];
    dateRange: {
      start?: string;
      end?: string;
    };
    scoreRange: {
      min: number;
      max: number;
    };
  };
  sortBy: 'createdAt' | 'submittedAt' | 'overallScore' | 'propertyName';
  sortOrder: 'asc' | 'desc';
  isLoading: boolean;
  isSubmitting: boolean;
  lastUpdated: string | null;
  drafts: Assessment[];
}

const initialState: AssessmentsState = {
  assessments: [],
  templates: [],
  currentAssessment: null,
  selectedTemplate: null,
  touchpointMediaCounts: {},
  filters: {
    department: [],
    status: [],
    dateRange: {},
    scoreRange: {
      min: 0,
      max: 100,
    },
  },
  sortBy: 'createdAt',
  sortOrder: 'desc',
  isLoading: false,
  isSubmitting: false,
  lastUpdated: null,
  drafts: [],
};

const assessmentsSlice = createSlice({
  name: 'assessments',
  initialState,
  reducers: {
    addAssessment: (state, action: PayloadAction<Assessment>) => {
      state.assessments.push(action.payload);
      state.lastUpdated = new Date().toISOString();
    },
    updateAssessment: (
      state,
      action: PayloadAction<{ id: string; updates: Partial<Assessment> }>
    ) => {
      const index = state.assessments.findIndex(
        a => a.id === action.payload.id
      );
      if (index !== -1) {
        state.assessments[index] = {
          ...state.assessments[index],
          ...action.payload.updates,
        };
        state.lastUpdated = new Date().toISOString();
      }
    },
    deleteAssessment: (state, action: PayloadAction<string>) => {
      state.assessments = state.assessments.filter(
        a => a.id !== action.payload
      );
      state.lastUpdated = new Date().toISOString();
    },
    setCurrentAssessment: (state, action: PayloadAction<Assessment | null>) => {
      state.currentAssessment = action.payload;
    },

    updateCurrentAssessmentAnswer: (
      state,
      action: PayloadAction<{
        answerId: string;
        standardId: number;
        answer: 'yes' | 'no' | 'na';
        comment?: string;
        answered_at?: string; // ISO 8601 timestamp
        touchpointId?: number;
        departmentId?: number;
      }>
    ) => {
      if (!state.currentAssessment) return;

      // Use standardId as the primary key since it's unique per question
      // Find by standardId first, then by answerId
      const existingItemIndex = state.currentAssessment.items.findIndex(
        item =>
          item.question_id_snapshot === action.payload.standardId ||
          item.id === action.payload.answerId
      );

      if (existingItemIndex !== -1) {
        state.currentAssessment.items[existingItemIndex] = {
          ...state.currentAssessment.items[existingItemIndex],
          id: action.payload.answerId, // Update with real answer_id if it changed
          answer: action.payload.answer,
          comment: action.payload.comment,
          answered_at: action.payload.answered_at,
          updated_at: new Date().toISOString(),
        };
      } else {
        state.currentAssessment.items.push({
          id: action.payload.answerId,
          assessment_id: state.currentAssessment.id,
          question_id_snapshot: action.payload.standardId,
          department_id_snapshot: action.payload.departmentId || 0,
          touchpoint_id_snapshot: action.payload.touchpointId || 0,
          answer: action.payload.answer,
          comment: action.payload.comment,
          answered_at: action.payload.answered_at,
          weight: 1.0,
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
          standard: '',
          description: '',
        });
      }
    },
    deleteTemplate: (state, action: PayloadAction<string>) => {
      state.templates = state.templates.filter(t => t.id !== action.payload);
    },
    clearFilters: state => {
      state.filters = {
        department: [],
        status: [],
        dateRange: {},
        scoreRange: { min: 0, max: 100 },
      };
    },
    // Media count management actions
    initializeTouchpointMediaCounts: (
      state,
      action: PayloadAction<{
        assessmentId: string;
        departmentId: number;
        touchpointId: number;
        currentImages: number;
        currentAudios: number;
      }>
    ) => {
      const key = `${action.payload.assessmentId}_${action.payload.departmentId}_${action.payload.touchpointId}`;
      state.touchpointMediaCounts[key] = {
        departmentId: action.payload.departmentId,
        touchpointId: action.payload.touchpointId,
        currentImages: action.payload.currentImages,
        currentAudios: action.payload.currentAudios,
        pendingImages: 0,
        pendingAudios: 0,
      };
    },
    incrementPendingMedia: (
      state,
      action: PayloadAction<{
        assessmentId: string;
        departmentId: number;
        touchpointId: number;
        mediaType: 'photo' | 'audio';
      }>
    ) => {
      const key = `${action.payload.assessmentId}_${action.payload.departmentId}_${action.payload.touchpointId}`;
      if (state.touchpointMediaCounts[key]) {
        if (action.payload.mediaType === 'photo') {
          state.touchpointMediaCounts[key].pendingImages += 1;
        } else if (action.payload.mediaType === 'audio') {
          state.touchpointMediaCounts[key].pendingAudios += 1;
        }
      }
    },
    clearAssessmentData: state => {
      state.currentAssessment = null;
      state.drafts = [];
      state.touchpointMediaCounts = {};
    },
  },
});

export const {
  addAssessment,
  updateAssessment,
  deleteAssessment,
  setCurrentAssessment,
  updateCurrentAssessmentAnswer,
  deleteTemplate,
  clearFilters,
  initializeTouchpointMediaCounts,
  incrementPendingMedia,
  clearAssessmentData,
} = assessmentsSlice.actions;

const assessmentsPersistConfig = {
  key: 'assessments',
  storage,
  whitelist: [
    'assessments',
    'templates',
    'drafts',
    'filters',
    'sortBy',
    'sortOrder',
    'lastUpdated',
  ],
};

export const assessmentsReducer = persistReducer(
  assessmentsPersistConfig,
  assessmentsSlice.reducer
);
export default assessmentsSlice.reducer;
